// Drift: Kundecenter, direkte beskeder og skabeloner på Supabase (del af SupabaseApi – se supabase.ts).
// Reglerne ligger i 20261029200000_kontor3.sql: kunden får et link (fra kontoret, eller ved at finde sin booking med
// bookingnummer + mobil), lægger billeder og dokumenter i den private bucket "kundesager" (<klient>/<token>/<fil>) og opretter
// sagen; kontoret ser sagerne med midlertidige links til filerne. Beskeder lægges i tabellen beskeder (spærren gælder).
// {{felter}} i kontorets beskeder flettes her med de samme funktioner som automatiseringerne (core/automationMotor.ts).
import { ApiFejl, type Api, type SkadesagVisning } from './api';
import type { Dokumentation, Skadesag } from '../../core/typer';
import { STANDARD_SKABELONER, lokalTilMs, udfyld, variabler, type Skabelon } from '../../core/automation';
import { autoKlient, kontekstFra } from '../../core/automationMotor';
import { STANDARD_FRISTER, ZONE_VINKLER, forhaandsvurdering, fristTimer, timerEfter, type Frister, type SagType } from '../../core/kundecenter';
import { RYKKER_SKABELONER } from '../../core/rykker';
import { email as tjekEmail, nummerplade as tjekPlade, nyKode, rens, rensTekst, telefon as tjekTelefon } from '../../core/validering';
import { tilVisning } from './sbKontor';
import type { SbKatalog, SbKontekst } from './sbKatalog';
import type { SbAutomation } from './sbAutomation';
import { SPAERRET_LANCERING, bygUdkast, kundeFelter, tjekFlere, udvaelg, type FlereSvar, type Modtager } from '../../core/flereBeskeder';
import { kanalFor, tjekBrug, workflowFor, workflowIdFor } from '../../core/skabelonBrug';
import { klokken } from '../../core/dato';

type R = Record<string, unknown>;
const BUCKET = 'kundesager';
const LAGER = 'lager:';
const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);
const tjek = (f: Record<string, string>) => { if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f); };
const TYPER = new Set(['fejl', 'skade', 'betaling', 'andet']);
const FILTYPER: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'application/pdf': 'pdf' };
type SagRaa = { sag: Skadesag; booking: Parameters<typeof tilVisning>[0] | null; dok: Dokumentation[]; kunde?: { bookinger: number; klagerFoer: number } | null };

export class SbKundecenter {
  constructor(private readonly c: SbKontekst, private readonly kat: SbKatalog, private readonly auto: () => SbAutomation) {}
  private async rpc<T>(navn: string, args: R = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  /** "lager:<sti>" → midlertidigt link (1 time) fra bucket'en. Andre værdier gives videre uændret. */
  private async links(bucket: string, vaerdier: (string | null | undefined)[]): Promise<Map<string, string>> {
    const stier = [...new Set(vaerdier.filter((x): x is string => !!x?.startsWith(LAGER)).map((x) => x.slice(LAGER.length)))];
    const ud = new Map<string, string>();
    if (!stier.length) return ud;
    const { data } = await this.c.sb.storage.from(bucket).createSignedUrls(stier, 3600);
    for (const x of data ?? []) if (x.path && x.signedUrl) ud.set(LAGER + x.path, x.signedUrl);
    return ud;
  }
  private static url(l: Map<string, string>, v: string) { return l.get(v) ?? (v.startsWith(LAGER) ? '' : v); }

  // ---------- Kundens side (uden login – tokenet er adgangen) ----------
  private async link(token: string) {
    return (await this.c.k(this.c.sb.rpc('kundecenter_link', { p_token: token }))) as {
      gyldig: boolean; aarsag?: string; klient?: string; frister?: Partial<Frister> | null;
      booking?: { id: number; ydelse: string; dato: string; slut: number | null; status: string } | null;
      forudfyldt?: { navn: string; telefon: string; email: string; nummerplade: string; booking: string };
    };
  }
  skadeLink: Api['skadeLink'] = async (token) => {
    const x = await this.link(token);
    if (!x.gyldig) return { gyldig: false, aarsag: x.aarsag };
    const b = x.booking ?? null;
    return {
      gyldig: true, frister: { ...STANDARD_FRISTER, ...(x.frister ?? {}) },
      timerSiden: b && b.slut != null && b.status === 'faerdig' ? timerEfter(lokalTilMs(b.dato, b.slut), Date.now()) : null,
      booking: b ? { id: b.id, ydelse: b.ydelse, dato: b.dato } : null, forudfyldt: x.forudfyldt,
    };
  };
  kundecenterAdgang: Api['kundecenterAdgang'] = async (booking, telefon) => {
    if (!tjekTelefon(telefon)) throw fejl('Skriv dit mobilnummer med 8 cifre.', { telefon: 'Skriv 8 cifre.' });
    const a = this.c.adresse();
    if (!a) throw fejl('Adressen findes ikke. Tjek, at den er skrevet rigtigt.');
    const token = await this.c.k(this.c.sb.rpc('kundecenter_adgang', { vaert: a.vaert, sti: a.slug, p_booking: rens(booking, 20), p_telefon: telefon }));
    return { token: token as string };
  };
  /** Lægger en fil (data-URL) i "kundesager" under <klient>/<token>/ og giver stien tilbage. */
  private async upload(klient: string, token: string, n: number, data: string): Promise<string> {
    const m = /^data:([a-z]+\/[a-z0-9.+-]+);base64,/.exec(data);
    const ext = m ? FILTYPER[m[1]] : undefined;
    if (!m || !ext) throw fejl('Filen skal være et billede (JPG, PNG, WEBP) eller en PDF.');
    const sti = `${klient}/${token}/${n}-${crypto.randomUUID()}.${ext}`;
    const blob = await (await fetch(data)).blob();
    const { error } = await this.c.sb.storage.from(BUCKET).upload(sti, blob, { contentType: m[1], upsert: false });
    if (error) throw fejl('En fil kunne ikke gemmes. Tjek forbindelsen, og prøv igen.');
    return sti;
  }
  opretSkade: Api['opretSkade'] = async (token, s) => {
    if (s.honning) return { id: 0 };
    const f: Record<string, string> = {};
    if (rens(s.navn, 100).length < 2) f.navn = 'Skriv dit navn.';
    if (!tjekTelefon(s.telefon)) f.telefon = 'Skriv et mobilnummer med 8 cifre.';
    if (!tjekEmail(s.email)) f.email = 'Skriv en gyldig e-mail.';
    if (rensTekst(s.beskrivelse, 3000).length < 10) f.beskrivelse = 'Beskriv kort, hvad der er sket (mindst 10 tegn).';
    if (!TYPER.has(s.type)) f.type = 'Vælg hvad sagen handler om.';
    if ((s.type === 'fejl' || s.type === 'skade') && s.billeder.length < 1) f.billeder = 'Tilføj mindst ét billede.';
    if (s.billeder.length > 12) f.billeder = 'Højst 12 billeder.';
    if (s.dokumenter.length > 5) f.dokumenter = 'Højst 5 dokumenter.';
    if ([...s.billeder, ...s.dokumenter.map((d) => d.data)].some((x) => x.length > 3_000_000)) f.billeder = 'Et af billederne er for stort.';
    tjek(f);
    const l = await this.link(token);
    if (!l.gyldig || !l.klient) throw fejl('Linket er ikke gyldigt længere. Start forfra.');
    let n = 0;
    const billeder = await Promise.all(s.billeder.map((b) => this.upload(l.klient!, token, ++n, b)));
    const dokumenter = await Promise.all(s.dokumenter.map(async (d) => ({ navn: rens(d.navn, 120), sti: await this.upload(l.klient!, token, ++n, d.data) })));
    const id = await this.c.k(this.c.sb.rpc('opret_kundesag', { p_token: token, p: {
      type: s.type, navn: rens(s.navn, 100), telefon: s.telefon, email: s.email, nummerplade: tjekPlade(s.nummerplade) ?? rens(s.nummerplade, 12).toUpperCase(),
      beskrivelse: rensTekst(s.beskrivelse, 3000), placering: s.placering.filter((p) => p in ZONE_VINKLER).slice(0, 17), billeder, dokumenter,
    } }));
    return { id: Number(id) };
  };

  // ---------- Kontoret ----------
  skadesager: Api['skadesager'] = async () => {
    const x = await this.rpc<{ frister: Partial<Frister> | null; sager: SagRaa[] }>('kundesager_liste');
    const frister: Frister = { ...STANDARD_FRISTER, ...(x.frister ?? {}) };
    const sager = x.sager ?? [];
    const [filer, billeder] = await Promise.all([
      this.links(BUCKET, sager.flatMap((r) => [...(r.sag.billeder ?? []), ...(r.sag.dokumenter ?? []).map((d) => d.data)])),
      this.links('steamer-billeder', sager.flatMap((r) => r.dok.map((d) => d.billede))),
    ]);
    return sager.map(({ sag, booking, dok, kunde }): SkadesagVisning => {
      const s: Skadesag = { ...sag, oprettet: String(sag.oprettet), billeder: (sag.billeder ?? []).map((b) => SbKundecenter.url(filer, b)).filter(Boolean),
        dokumenter: (sag.dokumenter ?? []).map((d) => ({ navn: d.navn, data: SbKundecenter.url(filer, d.data) })) };
      const b = booking ? tilVisning(booking) : null;
      const d = dok.map((x) => ({ ...x, billede: SbKundecenter.url(billeder, x.billede) })).filter((x) => x.billede);
      const vinkler = new Set(s.placering.flatMap((p) => ZONE_VINKLER[p] ?? []));
      const timer = b && b.slut != null && b.dato ? timerEfter(lokalTilMs(b.dato, b.slut), Date.parse(s.oprettet)) : null;
      const vurdering = forhaandsvurdering({
        type: s.type as SagType, beskrivelse: s.beskrivelse, antalBilleder: s.billeder.length, timerSidenUdfoert: fristTimer(s.type as SagType, frister) != null ? timer : null,
        frister, foerbillederIOmraadet: d.filter((x) => x.type === 'foer' && vinkler.has(x.vinkel)).length,
        tilvalg: b ? b.tilvalgNavne.map((t) => t.navn) : [], harRapport: s.dokumenter.length > 0, ai: s.ai ?? null,
      });
      return { ...s, booking: b, dokumentation: d, vurdering, timerEfter: timer, ...(kunde ? { kundeHistorik: { bookinger: Number(kunde.bookinger), klagerFoer: Number(kunde.klagerFoer) } } : {}) };
    });
  };
  sendSkadeLink: Api['sendSkadeLink'] = async (a) => {
    if (a.telefon && !tjekTelefon(a.telefon)) throw fejl('Tjek de markerede felter.', { telefon: 'Skriv et mobilnummer med 8 cifre.' });
    if (a.email && !tjekEmail(a.email)) throw fejl('Tjek de markerede felter.', { email: 'Skriv en gyldig e-mail.' });
    if (!a.booking_id && !a.telefon && !a.email) throw fejl('Skriv et mobilnummer eller en e-mail.', { telefon: 'Mangler.' });
    const link = await this.rpc<string>('send_kundecenter_link', { p_booking: a.booking_id ?? null, p_telefon: a.telefon ?? null, p_email: a.email ?? null, p_tekst: rensTekst(a.tekst ?? '', 600) });
    this.c.skift();
    return { link };
  };

  // ---------- Direkte beskeder ----------
  /** {{felter}} for en booking (samme som automatiseringerne) – eller for en kunde uden booking. */
  private async felter(booking_id: number | null, kunde_id: string | null): Promise<Record<string, string>> {
    if (booking_id) {
      const d = await this.rpc<{ raekke: Parameters<typeof kontekstFra>[0]; klient: Parameters<typeof autoKlient>[0] }>('besked_data', { p_booking: booking_id });
      return variabler(kontekstFra(d.raekke, autoKlient({ ...d.klient, workflows: null })));
    }
    const [ku, indst] = await Promise.all([
      this.c.k(this.c.sb.from('kunder').select('navn').eq('klient_id', await this.c.kraevKlient()).eq('id', kunde_id ?? '').maybeSingle()),
      this.kat.indstillinger(),
    ]);
    const navn = (ku as { navn: string } | null)?.navn;
    if (!navn) throw fejl('Vælg en kunde eller booking.');
    return { kunde_navn: navn, kunde_fornavn: navn.split(' ')[0], telefon: indst.firma.telefon, firma: indst.firma.navn };
  }
  udfyldTekst: Api['udfyldTekst'] = async (booking_id, tekster, kunde_id = null) => {
    if (!booking_id && !kunde_id) return tekster;
    const v = await this.felter(booking_id, kunde_id);
    return tekster.map((t) => udfyld(t, v));
  };
  /** Til flere: kontakt, samtykke og afmeldingsnøgle fra databasen → flettes pr. modtager her → send_beskeder lægger dem i kø
   *  (og tjekker spærre, samtykke og grænser igen). */
  sendBeskeder: Api['sendBeskeder'] = async (a) => {
    tjekFlere(a);
    const info = await this.rpc<{ spaerret: boolean; modtagere: Omit<Modtager, 'booking_id'>[] }>('besked_modtager_info', { p_kunder: [...new Set(a.modtagere.map((m) => m.kunde_id))] });
    if (info.spaerret) throw fejl(SPAERRET_LANCERING);
    const modtagere: Modtager[] = a.modtagere.map((x) => {
      const m = info.modtagere.find((y) => y.kunde_id === x.kunde_id);
      if (!m) throw fejl('Kunden findes ikke.');
      return { ...m, booking_id: x.booking_id };
    });
    const { send, sprunget } = udvaelg(modtagere, a.kanal, a.formaal);
    if (!send.length) return { sendt: 0, modtagere: 0, sprunget };
    const indst = await this.kat.indstillinger();
    const base = typeof location === 'undefined' ? '' : `${location.origin}${location.pathname}`;
    const udkast = [];
    for (const m of send) {
      const v = m.booking_id ? await this.felter(m.booking_id, null) : kundeFelter(m.navn, indst.firma.navn, indst.firma.telefon);
      udkast.push(...bygUdkast(m, a, v, base, indst.firma.navn));
    }
    const svar = await this.rpc<{ sendt: number; sprunget: FlereSvar['sprunget'] }>('send_beskeder', { p_formaal: a.formaal, p_skabelon: rens(a.skabelon ?? '', 120), p_beskeder: udkast });
    this.c.skift();
    const ude = new Set(svar.sprunget.map((x) => x.navn));
    return { sendt: svar.sendt, modtagere: send.filter((m) => !ude.has(m.navn)).length, sprunget: [...sprunget, ...svar.sprunget] };
  };
  beskedModtagere: Api['beskedModtagere'] = async (dato, steamer_id = null) => {
    type Raa = Modtager & { start: number | null; slut: number | null; steamer: string; adresse: string };
    const l = await this.rpc<Raa[]>('besked_modtagere', { p_dato: dato, p_steamer: steamer_id });
    return l.map(({ start, slut, steamer, adresse, ...m }) => ({ ...m,
      info: [start != null && slut != null ? `${klokken(start)}–${klokken(slut)}` : 'Tid aftales', steamer.split(' ')[0], adresse].filter(Boolean).join(' · ') }));
  };
  sendBesked: Api['sendBesked'] = async (a) => {
    if (!a.booking_id && !a.kunde_id) throw fejl('Vælg en kunde eller booking.');
    const tekst = rensTekst(a.tekst, 5000), sms = rensTekst(a.smsTekst ?? a.tekst, 1000), emne = rens(a.emne, 200);
    if ((a.kanal !== 'sms' && tekst.length < 2) || (a.kanal !== 'mail' && sms.length < 2)) throw fejl('Skriv en besked.');
    if (a.kanal !== 'sms' && emne.length < 2) throw fejl('Skriv et emne.', { emne: 'Skriv et emne.' });
    const v = await this.felter(a.booking_id ?? null, a.kunde_id ?? null);
    await this.rpc('send_besked', { p_booking: a.booking_id ?? null, p_kunde: a.booking_id ? null : a.kunde_id, p_kanal: a.kanal,
      p_emne: udfyld(emne, v), p_tekst: udfyld(tekst, v), p_sms: udfyld(sms, v) });
    this.c.skift();
  };
  sendTilDag: Api['sendTilDag'] = async (a) => {
    const l = (await this.c.k(this.c.sb.from('bookinger').select('id').eq('klient_id', await this.c.kraevKlient()).eq('steamer_id', a.steamer_id)
      .eq('dato', a.dato).in('status', ['godkendt', 'afventer', 'skal_rykkes', 'i_gang']).order('start'))) as { id: number }[];
    for (const b of l) await this.sendBesked({ booking_id: b.id, kanal: a.kanal, emne: a.emne, tekst: a.tekst, smsTekst: a.smsTekst });
    return { antal: l.length };
  };

  // ---------- Skabeloner (ingen rækker = standarden; første gang en gemmes, lægges standarden ind, så ingen forsvinder) ----------
  private async egne(k: string): Promise<Skabelon[]> {
    const l = (await this.c.k(this.c.sb.from('besked_skabeloner').select('id, navn, sms, emne, mail, steamer, kanal, workflow_id').eq('klient_id', k).order('sortering'))) as Skabelon[];
    return l.map(({ steamer, kanal, workflow_id, ...s }) => ({ ...s, ...(steamer ? { steamer } : {}), ...(kanal ? { kanal } : {}), ...(workflow_id ? { workflow_id } : {}) }));
  }
  /** Klientens rettede skabeloner (eller standarden) – bruges også af rykkerne. */
  async mineSkabeloner(): Promise<Skabelon[]> {
    const l = await this.egne(await this.c.kraevKlient());
    return l.length ? l : structuredClone(STANDARD_SKABELONER);
  }
  private async medStandard(k: string): Promise<Skabelon[]> {
    const l = await this.egne(k);
    if (l.length) return l;
    await this.c.k(this.c.sb.from('besked_skabeloner').insert(STANDARD_SKABELONER.map((s, i) => ({ klient_id: k, id: s.id, navn: s.navn, sms: s.sms, emne: s.emne, mail: s.mail, steamer: !!s.steamer, sortering: i + 1 }))));
    return structuredClone(STANDARD_SKABELONER);
  }
  skabeloner: Api['skabeloner'] = async () => {
    const l = await this.mineSkabeloner();
    return [...l, ...RYKKER_SKABELONER.filter((r) => !l.some((x) => x.id === r.id))];
  };
  gemSkabelon: Api['gemSkabelon'] = async (t, brug) => {
    const navn = rens(t.navn, 60);
    if (navn.length < 2) throw fejl('Skriv et navn.', { navn: 'Skriv et navn.' });
    if (brug) { const f = tjekBrug(brug); if (Object.keys(f).length) throw fejl('Tjek hvornår skabelonen sendes.', f); }
    const k = await this.c.kraevKlient();
    const l = await this.medStandard(k);
    const id = t.id || 'sk_' + nyKode(5).toLowerCase();
    const i = l.findIndex((x) => x.id === id);
    const kanal = t.steamer ? 'sms' : t.kanal ?? kanalFor(t);
    let workflow_id = t.workflow_id ?? null;
    // Automatisk: automatiseringen oprettes eller rettes (core/skabelonBrug.ts); kun manuelt: en tilknyttet automatisering slettes
    if (brug) {
      const a = this.auto();
      const gl = (await a.workflows()).find((w) => w.id === (workflow_id ?? workflowIdFor(id))) ?? null;
      const w = workflowFor({ ...t, id, navn, kanal }, brug, gl);
      await a.gemWorkflow(w);
      workflow_id = w.id;
    } else if (brug === null && workflow_id) {
      await this.auto().sletWorkflow(workflow_id);
      workflow_id = null;
    }
    await this.c.k(this.c.sb.from('besked_skabeloner').upsert({ klient_id: k, id, navn, sms: rensTekst(t.sms ?? '', 1000), emne: rens(t.emne ?? '', 200),
      mail: rensTekst(t.mail ?? '', 5000), steamer: !!t.steamer, kanal, workflow_id, sortering: i >= 0 ? i + 1 : l.length + 1 }, { onConflict: 'klient_id,id' }));
    this.c.skift();
    return { id, workflow_id };
  };
  sletSkabelon: Api['sletSkabelon'] = async (id) => {
    const k = await this.c.kraevKlient();
    const l = await this.medStandard(k);
    const s = l.find((x) => x.id === id);
    if (!s) throw fejl('Skabelonen findes ikke.');
    if (s.workflow_id) await this.auto().sletWorkflow(s.workflow_id);
    await this.c.k(this.c.sb.from('besked_skabeloner').delete().eq('klient_id', k).eq('id', id));
    this.c.skift();
  };
}
